import { CloudDownloadOutlined } from "@ant-design/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid } from "@mui/material";
import { SelectField } from "components/@extended/SelectField";
import { IMemberReports, MemberReportsSchema } from "../api/types";
import { useForm } from "react-hook-form";
import { IDropdownModel } from "common/models/IDropdownModel";
import { useEffect, useState } from "react";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import { GetMemberStatusValues } from "features/memberStatus/api/hooks";
import RadioButtonGroup from "components/RadioButtonGroup";
import { ProgramModelForDropdown } from "features/program/api/types";
import { GetProgramByInstituteDepartment } from "features/program/api/hooks";
import api from "utils/api";
import AnimateButton from "components/@extended/AnimateButton";
import LoadingButton from "@mui/lab/LoadingButton";
import MainCard from "components/MainCard";
import { GetAdmissionYearValues } from "features/admissionYear/api/hooks";
import { ExportExcelDialog } from "components/ExportExcelDialog";
import { useGetExportFields } from "common/models/hooks/useGetExportFields";

const MemberReports = () => {
  const groupName = "MemberReport";
  const fileName = "MemberReports";
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingFilterData, setPendingFilterData] = useState<any>(null);
  const [programModel, setProgramModel] = useState<ProgramModelForDropdown>();

  const { data: columns = [], isLoading: columnsLoading } =
    useGetExportFields(groupName);

  const {
    control,
    reset,
    setValue,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<IMemberReports>({
    resolver: zodResolver(MemberReportsSchema),
    defaultValues: {
      filterRadioBtn: 1,
      libRadioBtn: 1,
      memberTypeRadioBtn: 1,
    },
  });

  const { data: admissionYearQuery } = GetAdmissionYearValues();
  const [admissionYearOptions, setAdmissionYearOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setAdmissionYearOptions(admissionYearQuery?.apiData || []);
  }, [admissionYearQuery]);

  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const instituteID = watch("instituteID");
  const departmentID = watch("departmentID");

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(instituteID);
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  useEffect(() => {
    if (departmentID == null || departmentID == undefined) {
      setProgramOptions([]);
      setValue("programID", null);
    }

    if (instituteID == null || instituteID == undefined) {
      setDepartmentOptions([]);
      setProgramOptions([]);
      setValue("departmentID", null);
    }
  }, [instituteID, departmentID]);

  useEffect(() => {
    setProgramModel({
      instituteID: instituteID,
      departmentID: departmentID,
    });
  }, [instituteID, departmentID]);

  const { data: programQuery } = GetProgramByInstituteDepartment(programModel);
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);

  useEffect(() => {
    if (programQuery?.status == 200) {
      setProgramOptions(programQuery?.apiData || []);
    }
  }, [programQuery]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: memberStatusQuery } = GetMemberStatusValues();
  const [memberStatusOptions, setMemberStatusOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMemberStatusOptions(memberStatusQuery?.apiData || []);
  }, [memberStatusQuery]);

  const onSubmit = handleSubmit((data) => {
    if (isSubmitting) return;
    setPendingFilterData(data);
    setDialogOpen(true);
  });

  const handleExport = (selectedFields: string[]) => {
    setLoading(true);
    const payload = {
      groupName,
      fileName,
      reportTitle: "Library Member Report",
      exportFields: selectedFields,
      parameters: pendingFilterData || {},
      ...pendingFilterData,
    };

    api.downloadExcel(
      "api/GenericExport/download",
      fileName,
      payload,
      (isLoading) => {
        setLoading(isLoading);
        if (!isLoading) {
          setDialogOpen(false);
        }
      },
    );
  };

  const handleClearFilter = () => {
    reset();
  };

  return (
    <>
      <MainCard darkTitle title="Member Export" darktitleVariant={"h4"}>
        <Box component={"form"} onSubmit={onSubmit}>
          <Grid container direction={"row"} spacing={2}>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                options={admissionYearOptions}
                control={control}
                name="admissionYearID"
                label="Student Admission Year"
                placeholder="Select Student Admission Year"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                control={control}
                name="instituteID"
                options={instituteOptions}
                label="Institute"
                placeholder="Select Institute"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                control={control}
                name="departmentID"
                options={departmentOptions}
                label="Department"
                placeholder="Select Department"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                control={control}
                name="programID"
                options={programOptions}
                label="Program"
                placeholder="Select Program"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                control={control}
                name="memberStatusID"
                options={memberStatusOptions}
                label="Member Status"
                placeholder="Select Member Status"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <SelectField
                control={control}
                name="memberTypeID"
                options={memberTypeOptions}
                label="Member Type"
                placeholder="Select Member Type"
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <RadioButtonGroup
                row
                control={control}
                name="filterRadioBtn"
                options={[
                  { id: "1", label: "All" },
                  { id: "2", label: "Activate" },
                  { id: "3", label: "De-Activate" },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <RadioButtonGroup
                row
                control={control}
                name="libRadioBtn"
                options={[
                  { id: "1", label: "Current Library" },
                  { id: "2", label: "All Library" },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3}>
              <RadioButtonGroup
                row
                control={control}
                name="memberTypeRadioBtn"
                options={[
                  { id: "1", label: "All Members" },
                  { id: "2", label: "No Due Certified Members" },
                ]}
              />
            </Grid>

            <Grid
              container
              item
              direction={"row"}
              xs={12}
              md={12}
              lg={12}
              alignContent={"flex-end"}
            >
              <AnimateButton>
                <LoadingButton
                  disableElevation
                  disabled={isSubmitting || columnsLoading}
                  type="submit"
                  fullWidth
                  variant="contained"
                  loading={loading || columnsLoading}
                  loadingPosition="start"
                  startIcon={<CloudDownloadOutlined />}
                >
                  Download
                </LoadingButton>
              </AnimateButton>
              <Button
                title="Clear"
                type="reset"
                sx={{ mx: 1 }}
                variant="contained"
                color="secondary"
                onClick={handleClearFilter}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      </MainCard>

      <ExportExcelDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        columns={columns}
        onExport={handleExport}
        loading={loading}
      />
    </>
  );
};

export default MemberReports;
